/**
 * useResolveRunReviews — mark a batch of open agent run reviews `confirmed`
 * (a real defect) or `dismissed` (noise), with a required reason (#4966).
 *
 * Goes through the `agentInfra_resolveRunReviews` callable (admin-gated
 * server-side; the collection is deny-all to clients). Optimistic: the rows
 * change status immediately and roll back if the call fails. The server
 * reports ids it skipped (already resolved, or not found), which the caller
 * shows to the reviewer.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpsCallable } from "../../lib/firebase/authReadyCallable";
import { invokeNamedCallable } from "../../lib/telemetry/namedCallable";
import { functions } from "../../config/firebase";
import {
  AGENT_RUN_REVIEWS_QUERY_KEY,
  OPEN_REVIEW_STATUSES,
  agentRunReviewKeys,
  type AgentRunReviewsCache,
} from "./useAgentRunReviews";

export type ReviewResolution = "confirmed" | "dismissed";

export interface ResolveRunReviewsInput {
  runIds: string[];
  status: ReviewResolution;
  reason: string;
}

export interface ResolveRunReviewsResult {
  updated: string[];
  skipped: Array<{ runId: string; reason: string }>;
}

/** Server batch cap — the UI splits nothing; it refuses selections above this. */
export const MAX_RESOLVE_BATCH = 200;

export function useResolveRunReviews() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ResolveRunReviewsInput): Promise<ResolveRunReviewsResult> => {
      const fn = httpsCallable<ResolveRunReviewsInput, ResolveRunReviewsResult>(
        functions,
        "agentInfra_resolveRunReviews"
      );
      const result = await invokeNamedCallable("agentInfra_resolveRunReviews", () => fn(input));
      return result.data;
    },
    onMutate: async ({ runIds, status, reason }) => {
      await queryClient.cancelQueries({ queryKey: agentRunReviewKeys.all });
      const previous = queryClient.getQueryData<AgentRunReviewsCache>(AGENT_RUN_REVIEWS_QUERY_KEY);
      const ids = new Set(runIds);
      queryClient.setQueryData<AgentRunReviewsCache>(AGENT_RUN_REVIEWS_QUERY_KEY, (old) =>
        old ?
          {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              reviews: page.reviews.map((r) =>
                ids.has(r.runId) && OPEN_REVIEW_STATUSES.has(r.status) ?
                  { ...r, status, resolutionReason: reason } :
                  r
              ),
            })),
          } :
          old
      );
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) queryClient.setQueryData(AGENT_RUN_REVIEWS_QUERY_KEY, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: agentRunReviewKeys.all }),
  });
}
